// import components
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';
import ColorCell from 'components/shared/Table/TableCell/DataGrid/ColorCell';
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import { singleSelectValueFormatter } from 'components/shared/DataGrid/utils/valueFormatters/singleSelectValueFormatter';

// import external libraries
import { toInteger } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import hooks
import { useGetDccOptions } from '../components/Sections/TimeBoundValuesSection/hooks/useGetDccOptions';

// import types
import { DutyHourTypeDynFlag, DutyHourTypeFlag } from 'generated';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';

// import utils
import { nestedFieldValueGetter } from 'utils/grid';
import mapToOptions from 'utils/mapToOptions';

export const useGetColumns = () => {
  const { t } = useTranslation('common');
  const { data: dccOptions, isFetching: isDccOptionsFetching } =
    useGetDccOptions();

  return useMemo<GridColDef<GridValidRowModel>[]>(
    () => [
      {
        headerName: t('dutyHourTypes.column.code'),
        field: 'code',
        type: 'string',
        hideable: false,
        minWidth: 100
      },
      {
        headerName: t('dutyHourTypes.column.id'),
        field: 'dutyTypeId',
        type: 'string',
        minWidth: 100
      },
      {
        headerName: t('dutyHourTypes.column.description'),
        field: 'description',
        flex: 1,
        type: 'string',
        minWidth: 260
      },
      {
        headerName: t('dutyHourTypes.column.dcc'),
        field: 'dcc.dccCode',
        type: 'singleSelect',
        flex: 1,
        minWidth: 260,
        filterable: !isDccOptionsFetching,
        valueOptions: mapToOptions(dccOptions ?? [], 'value', 'label'),
        valueGetter: (_, row) =>
          toInteger(nestedFieldValueGetter('dcc.dccCode', row) as string),
        valueFormatter: (params) =>
          singleSelectValueFormatter(params, dccOptions ?? [])
      },
      {
        headerName: t('validFrom'),
        field: 'valid.from',
        minWidth: 110,
        type: 'date',
        valueFormatter: dateFormatter,
        valueGetter: (_, row) =>
          toInteger(nestedFieldValueGetter('valid.from', row))
      },
      {
        headerName: t('validTo'),
        field: 'valid.to',
        minWidth: 110,
        type: 'date',
        valueFormatter: dateFormatter,
        valueGetter: (_, row) =>
          toInteger(nestedFieldValueGetter('valid.to', row))
      },
      {
        headerName: t('dutyHourTypes.column.color'),
        field: 'colorCode',
        align: 'center',
        minWidth: 50,
        renderCell: ({ value }) => <ColorCell value={value} />
      },
      {
        headerName: t('dutyHourTypes.column.default'),
        field: 'DEFAULT' as DutyHourTypeFlag,
        type: 'boolean',
        minWidth: 50,
        valueGetter: (_, row) => row?.dutyTypeFlags?.includes('DEFAULT'),
        renderCell: ({ value }) => {
          return <BooleanCell value={value} />;
        }
      },
      {
        headerName: t('dutyHourTypes.column.decentralized'),
        field: 'AVAILABLE_DECENTRALIZED' as DutyHourTypeDynFlag,
        type: 'boolean',
        minWidth: 50,
        valueGetter: (_, row) =>
          row?.flags?.includes('AVAILABLE_DECENTRALIZED'),
        renderCell: ({ value }) => <BooleanCell value={value} />
      }
    ],
    [dccOptions, isDccOptionsFetching]
  );
};
